import {
  gql,
  LazyQueryResultTuple,
  MutationTuple,
  QueryResult,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'
import {
  EdgeInput,
  NodeInput,
  Pipeline,
  PipelineInput,
  SliceStatus,
  SliceStatusInput,
  StepType,
} from '@ui/__generated__/graphql'

const DefaultPipelineWorkflowQL = gql`
  query DefaultPipelineWorkflow {
    defaultPipelineWorkflow {
      id
      name
      enabled
      nodes {
        id
        type
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          size {
            span
            log
            metric
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            logsFailure
            spansFailure
            metricsFailure
          }
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
        }
      }
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
      steps {
        id
        stepType {
          id
          type
          description
          logsEnabled
          spansEnabled
          metricsEnabled
        }
        predicate
        config
        logsEnabled
        spansEnabled
        metricsEnabled
        order
        nodeId
      }
    }
  }
`

const CreatePipelineWorkflowQL = gql`
  mutation CreatePipelineWorkflow($name: String!, $userId: String, $enabled: Boolean) {
    createPipelineWorkflow(name: $name, userId: $userId, enabled: $enabled) {
      enabled
      name
      id
      nodes {
        id
        type
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          size {
            span
            log
            metric
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            successPercentage
            errors {
              message
              timestamp
            }
          }
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
        }
      }
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
    }
  }
`

export const UpdatePipelineWorkflowQL = gql`
  mutation UpdatePipelineWorkflow($pipeline: PipelineInput!, $nodes: [NodeInput]!, $edges: [EdgeInput]!) {
    updatePipelineWorkflow(pipeline: $pipeline, nodes: $nodes, edges: $edges) {
      id
      name
      enabled
      nodes {
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            logsFailure
            spansFailure
            metricsFailure
          }
        }
      }
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
    }
  }
`

export const UpdatePipelineWorkflowAndSourcesQL = gql`
  mutation UpdatePipelineWorkflowAndSources(
    $pipeline: PipelineInput!
    $nodes: [NodeInput]!
    $edges: [EdgeInput]!
    $sourceIds: [Int!]!
  ) {
    updatePipelineWorkflow(pipeline: $pipeline, nodes: $nodes, edges: $edges, sourceIds: $sourceIds) {
      id
      name
      enabled
      nodes {
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            logsFailure
            spansFailure
            metricsFailure
          }
        }
      }
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
    }
  }
`

const DeletePipelineWorkflowQL = gql`
  mutation DeletePipelineWorkflow($pipelineId: String!) {
    deletePipelineWorkflow(pipelineId: $pipelineId)
  }
`

const GetPipelineQL = gql`
  query GetPipelineWorkflow($pipelineId: String!) {
    pipelineWorkflow(pipeline_id: $pipelineId) {
      id
      name
      enabled
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
    }
  }
`

const GetAllPipelinesQL = gql`
  query AllPipelineWorkflows {
    allPipelineWorkflows {
      id
      name
      enabled
      nodes {
        id
        type
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          size {
            span
            log
            metric
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            successPercentage
            errors {
              message
              timestamp
            }
          }
        }
      }
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
    }
  }
`

const UpdatePipelineQL = gql`
  mutation UpdatePipelines($pipeline: PipelineInput!) {
    updatePipeline(pipeline: $pipeline) {
      id
      name
      enabled
      createdTimestamp
      lastUpdatedTimestamp
      createdBy
      lastUpdatedBy
      isPrimary
    }
  }
`

const DeletePipelineQL = gql`
  mutation DeletePipelineWorkflow($pipelineId: String!) {
    deletePipelineWorkflow(pipelineId: $pipelineId)
  }
`

const GetNodesAndEdgesQL = gql`
  query NodesAndEdges($pipelineId: String!) {
    pipelineWorkflow(pipeline_id: $pipelineId) {
      id
      name
      enabled
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
      nodes {
        id
        type
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          size {
            span
            log
            metric
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            successPercentage
            errors {
              message
              timestamp
            }
          }
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
        }
      }
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
    }
  }
`

const GetNodesAndEdgesWithSourcesQL = gql`
  query NodesAndEdgesWithSources($pipelineId: String!) {
    pipelineWorkflow(pipeline_id: $pipelineId) {
      id
      name
      enabled
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
      nodes {
        id
        type
        data {
          pipelineId
          description
          code
          exportType
          createdTimestamp
          updatedTimestamp
          createdBy
          updatedBy
          destinationId
          size {
            span
            log
            metric
          }
          status {
            logsEnabled
            spansEnabled
            metricsEnabled
            logsStatus
            spansStatus
            metricsStatus
            successPercentage
            errors {
              message
              timestamp
            }
          }
          steps {
            id
            stepType {
              id
              type
              description
              logsEnabled
              spansEnabled
              metricsEnabled
            }
            predicate
            config
            logsEnabled
            spansEnabled
            metricsEnabled
            order
            nodeId
          }
        }
      }
      edges {
        id
        type
        source
        data {
          pipelineId
          createdTimestamp
          lastUpdatedTimestamp
          createdBy
          lastUpdatedBy
        }
        target
        sourceHandle
        targetHandle
      }
      assignedSources {
        id
        name
        type
      }
    }
  }
`

export const UpdateNodeStatusQL = gql`
  mutation UpdateNodeStatus($nodeId: String!, $status: SliceStatusInput!) {
    updateNodeStatus(nodeId: $nodeId, status: $status) {
      logsEnabled
      spansEnabled
      metricsEnabled
      logsStatus
      spansStatus
      metricsStatus
      successPercentage
      logsFailure
      spansFailure
      metricsFailure
      errors {
        message
        timestamp
      }
    }
  }
`

const GetNodeStepsQL = gql`
  query StepTypes {
    stepTypes {
      id
      type
      description
      logsEnabled
      spansEnabled
      metricsEnabled
    }
  }
`

export const GetDefaultPipelineWorkflowLazyQuery = (): LazyQueryResultTuple<
  {
    defaultPipelineWorkflow: Pipeline
  },
  Record<string, never>
> => {
  return useLazyQuery<{ defaultPipelineWorkflow: Pipeline }, Record<string, never>>(DefaultPipelineWorkflowQL, {
    fetchPolicy: 'network-only',
  })
}

export const CreatePipelineWorkflowMutation = (): MutationTuple<
  { createPipelineWorkflow: Pipeline },
  { name: string; userId?: string; enabled?: boolean }
> => {
  return useMutation<{ createPipelineWorkflow: Pipeline }, { name: string; userId?: string; enabled?: boolean }>(
    CreatePipelineWorkflowQL,
    {
      fetchPolicy: 'network-only',
    }
  )
}

export const UpdatePipelineWorkflowMutation = (): MutationTuple<
  { updatePipelineWorkflow: Pipeline },
  { nodes: NodeInput[]; edges: EdgeInput[]; userId?: string; pipeline: PipelineInput }
> => {
  return useMutation<
    { updatePipelineWorkflow: Pipeline },
    { nodes: NodeInput[]; edges: EdgeInput[]; userId?: string; pipeline: PipelineInput }
  >(UpdatePipelineWorkflowQL, {
    fetchPolicy: 'network-only',
  })
}

export const UpdatePipelineWorkflowAndSourcesMutation = (): MutationTuple<
  { updatePipelineWorkflow: Pipeline },
  { nodes: NodeInput[]; edges: EdgeInput[]; userId?: string; pipeline: PipelineInput; sourceIds?: number[] }
> => {
  return useMutation<
    { updatePipelineWorkflow: Pipeline },
    { nodes: NodeInput[]; edges: EdgeInput[]; userId?: string; pipeline: PipelineInput; sourceIds?: number[] }
  >(UpdatePipelineWorkflowAndSourcesQL, {
    fetchPolicy: 'network-only',
  })
}

export const DeletePipelineWorkflowMutation = (): MutationTuple<
  { deletePipelineWorkflow: boolean },
  { pipelineId: string }
> => {
  return useMutation<{ deletePipelineWorkflow: boolean }, { pipelineId: string }>(DeletePipelineWorkflowQL, {
    fetchPolicy: 'network-only',
  })
}

export const GetPipelineQuery = ({
  id,
}: {
  id: string
}): QueryResult<{ pipelineWorkflow: Pipeline }, { pipelineId: string }> => {
  return useQuery<{ pipelineWorkflow: Pipeline }, { pipelineId: string }>(GetPipelineQL, {
    fetchPolicy: 'network-only',
    variables: {
      pipelineId: id,
    },
  })
}

export const GetPipelineLazyQuery = (): LazyQueryResultTuple<
  { pipelineWorkflow: Pipeline },
  { pipelineId: string }
> => {
  return useLazyQuery<{ pipelineWorkflow: Pipeline }, { pipelineId: string }>(GetPipelineQL, {
    fetchPolicy: 'network-only',
  })
}

export const GetAllPipelinesQuery = (): QueryResult<{ allPipelineWorkflows: Pipeline[] }, Record<string, never>> => {
  return useQuery<{ allPipelineWorkflows: Pipeline[] }, Record<string, never>>(GetAllPipelinesQL, {
    fetchPolicy: 'network-only',
  })
}

export const GetAllPipelinesLazyQuery = (): LazyQueryResultTuple<
  { allPipelineWorkflows: Pipeline[] },
  Record<string, never>
> => {
  return useLazyQuery<{ allPipelineWorkflows: Pipeline[] }, Record<string, never>>(GetAllPipelinesQL, {
    fetchPolicy: 'network-only',
  })
}

export const UpdatePipelineMutation = (): MutationTuple<{ updatePipeline: Pipeline }, { pipeline: PipelineInput }> => {
  return useMutation<{ updatePipeline: Pipeline }, { pipeline: PipelineInput }>(UpdatePipelineQL, {
    fetchPolicy: 'network-only',
  })
}

export const DeletePipelineMutation = (): MutationTuple<
  { deletePipelineWorkflow: boolean },
  { pipelineId: string }
> => {
  return useMutation<{ deletePipelineWorkflow: boolean }, { pipelineId: string }>(DeletePipelineQL, {
    fetchPolicy: 'network-only',
  })
}

export const GetNodesAndEdgesLazyQuery = (): LazyQueryResultTuple<
  { pipelineWorkflow: Pipeline },
  { pipelineId: string }
> => {
  return useLazyQuery<{ pipelineWorkflow: Pipeline }, { pipelineId: string }>(GetNodesAndEdgesQL, {
    fetchPolicy: 'network-only',
  })
}

export const GetNodesAndEdgesWithSourcesLazyQuery = (): LazyQueryResultTuple<
  { pipelineWorkflow: Pipeline },
  { pipelineId: string }
> => {
  return useLazyQuery<{ pipelineWorkflow: Pipeline }, { pipelineId: string }>(GetNodesAndEdgesWithSourcesQL, {
    fetchPolicy: 'network-only',
  })
}

export const UpdateNodeStatusMutation = (): MutationTuple<
  { updateNodeStatus: SliceStatus },
  { nodeId: string; status: SliceStatusInput }
> => {
  return useMutation<{ updateNodeStatus: SliceStatus }, { nodeId: string; status: SliceStatusInput }>(
    UpdateNodeStatusQL,
    {
      fetchPolicy: 'network-only',
    }
  )
}

export const GetNodeStepsQuery = (): QueryResult<{ stepTypes: StepType[] }> => {
  return useQuery<{ stepTypes: StepType[] }>(GetNodeStepsQL, {
    fetchPolicy: 'network-only',
  })
}
